import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
  label?: string;
}

interface State {
  error: Error | null;
}

/** Keeps a malformed problem row from white-screening the whole dashboard. */
export class QBErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(
      `[QuestionBank${this.props.label ? `/${this.props.label}` : ""}] render error`,
      error,
      info.componentStack,
    );
  }

  private reset = () => this.setState({ error: null });

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="m-4 rounded-lg border border-red-200 bg-red-50 p-4">
        <div className="text-sm font-bold text-red-800">
          This panel failed to render
          {this.props.label ? ` (${this.props.label})` : ""}
        </div>
        <p className="mt-1 text-xs text-red-700">{error.message}</p>
        <pre className="mt-2 max-h-40 overflow-auto rounded bg-white/60 p-2 text-[10px] text-red-900">
          {error.stack}
        </pre>
        <Button size="sm" variant="outline" className="mt-3" onClick={this.reset}>
          Try again
        </Button>
      </div>
    );
  }
}
